import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { css, StyleSheet } from 'aphrodite';
import { Button } from 'react-bootstrap';

import placeholderProductImage from 'images/placeholder-img-product-v2.svg';
import { formatGMV } from 'lib/utils/FormatUtils';
import {
  BG_2,
  BORDER_2,
  FG_1,
  FG_3,
  FONT_BODY,
  FONT_SIZE_SMALL,
  FONT_SIZE_XS,
  FONT_WEIGHT_BOLD,
  FONT_WEIGHT_MEDIUM,
  FONT_WEIGHT_SEMIBOLD,
  PRIMARY_COLOR,
  PRIMARY_LIGHT_RGBA,
  RADIUS_LG,
  SHADOW_XS,
  WHITE,
} from 'components/upb_viewer_beta/css/betaTokens';

const DASH = '—';

/** Logo sources tried in priority order; local placeholder is the final fallback. */
function logoCandidates(item) {
  return [item.logo_url, placeholderProductImage].filter(Boolean);
}

/** Logo <img> with a fallback chain — advances on load error, resets per item. */
function MsvLogo({ item, className }) {
  const candidates = useMemo(() => logoCandidates(item), [item]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    setIdx(0);
  }, [item]);

  const handleError = () => {
    if (idx < candidates.length - 1) setIdx(idx + 1);
  };

  return <img src={candidates[idx]} alt="" className={className} onError={handleError} />;
}

MsvLogo.propTypes = {
  item: PropTypes.object.isRequired,
  className: PropTypes.string,
};

MsvLogo.defaultProps = { className: undefined };

function fmtCount(value) {
  if (value == null) return DASH;
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString('en-US') : DASH;
}

function StatRow({ label, value }) {
  return (
    <div className={css(styles.stat)}>
      <span className={css(styles.statLabel)}>{label}</span>
      <span className={css(styles.statValue)}>{value}</span>
    </div>
  );
}

StatRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

/**
 * MsvCard — Manufacturer Sales Viewer tile, used for both manufacturers and
 * brands (both payloads share { name, logo_url, annualized_monitizable_gmv,
 * distributor_count, operator_count }). Matches the mockup's mfr-card: logo on
 * top + name + a stacked stat block (Annualized GMV / Distributors / Operators).
 */
function MsvCard({ item, onClick }) {
  const name = item.name || DASH;
  const gmv = item.annualized_monitizable_gmv ? formatGMV(item.annualized_monitizable_gmv) : DASH;

  return (
    <Button
      type="button"
      variant="link"
      className={css(styles.card)}
      onClick={() => onClick && onClick(item)}
      title={name}
    >
      <div className={css(styles.logoBox)}>
        <MsvLogo item={item} className={css(styles.logo)} />
      </div>
      <div className={css(styles.body)}>
        <div className={css(styles.name)}>{name}</div>
        <div className={css(styles.stats)}>
          <StatRow label="Annualized GMV" value={gmv} />
          <StatRow label="Distributors" value={fmtCount(item.distributor_count)} />
          <StatRow label="Operators" value={fmtCount(item.operator_count)} />
        </div>
      </div>
    </Button>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: WHITE,
    border: `1px solid ${BORDER_2}`,
    borderRadius: RADIUS_LG,
    boxShadow: SHADOW_XS,
    padding: 0,
    cursor: 'pointer',
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    ':hover': {
      borderColor: PRIMARY_COLOR,
      boxShadow: `0 0 0 3px ${PRIMARY_LIGHT_RGBA}, ${SHADOW_XS}`,
    },
  },

  logoBox: {
    height: 130,
    overflow: 'hidden',
    backgroundColor: BG_2,
    borderBottom: `1px solid ${BORDER_2}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
    flexShrink: 0,
  },

  logo: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
  },

  body: {
    padding: '10px 12px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    minWidth: 0,
    flex: 1,
  },

  name: {
    fontFamily: FONT_BODY,
    fontWeight: FONT_WEIGHT_SEMIBOLD,
    fontSize: FONT_SIZE_SMALL,
    color: FG_1,
    lineHeight: 1.3,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    minHeight: 34,
  },

  stats: {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
    borderTop: `1px solid ${BORDER_2}`,
    paddingTop: 8,
    marginTop: 'auto',
  },

  stat: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 6,
  },

  statLabel: {
    fontFamily: FONT_BODY,
    fontWeight: FONT_WEIGHT_MEDIUM,
    fontSize: FONT_SIZE_XS,
    color: FG_3,
  },

  statValue: {
    fontFamily: FONT_BODY,
    fontWeight: FONT_WEIGHT_BOLD,
    fontSize: FONT_SIZE_XS,
    color: FG_1,
    fontVariantNumeric: 'tabular-nums',
  },
});

MsvCard.propTypes = {
  item: PropTypes.object.isRequired,
  onClick: PropTypes.func,
};

MsvCard.defaultProps = {
  onClick: null,
};

export default MsvCard;
